import {useAccessToken} from "../../../services/auth"
import {useAsyncResult} from "../../../hooks/useAsyncResult"
import {Org} from "../../../resources/org"
import React from "react"
import {MainSection} from "../../../containers/MainSection/MainSection"
import {Customer} from "../../../resources/customer"
import classNames from "classnames"
import {useQueryState} from "use-location-state"
import OrgOverview from "../OrgOverview"
import {isSandboxOrLocalEnv} from "../../../utilities/environment"
import {ClientOrgBankAgreement, findClientOrgBankAgreement} from "../../../resources/clientOrgBankAgreement"
import {BankNavigationsTabs} from "./BankNavigationsTabs"
import {OnboardingRulesNavigationsTabContent} from "./OnboardingRulesNavigationsTabContent"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import OrgTitleBar from "../OrgPageComponents/OrgTitleBar"
import OrgPageWithoutOnboardingRules from "../OrgPageComponents/OrgPageWithoutOnboardingRules"
import Loading from "../../../components/Loading/Loading"

interface PageProps {
	customers: Customer[]
	org: Org
	currentDefaultBankName?: string
	bankNames?: string[]
	refresh: () => void
	refreshToken: number
	clientOrgBankAgreements: ClientOrgBankAgreement[]
}

const Page = ({
	customers,
	org,
	currentDefaultBankName,
	bankNames,
	refresh,
	refreshToken,
	clientOrgBankAgreements,
}: PageProps) => {
	const showOnboardingRulesTabs = isSandboxOrLocalEnv()
	const [currentTab, setCurrentTab] = useQueryState<string>("tab", "Overview")
	return showOnboardingRulesTabs ? (
		<>
			<OrgTitleBar org={org} customers={customers} refresh={refresh} />
			<nav className="org-settings tabs-container">
				<div className="tabs">
					<ul>
						<li
							className={classNames(currentTab == "Overview" && "is-active")}
							onClick={() => setCurrentTab("Overview")}
						>
							<a>Overview</a>
						</li>
						{clientOrgBankAgreements.length > 1 ? (
							<BankNavigationsTabs
								clientOrgBankAgreements={clientOrgBankAgreements}
								currentTab={currentTab}
								setCurrentTab={setCurrentTab}
							/>
						) : (
							<li
								className={classNames(currentTab === "Onboarding Rules" && "is-active")}
								onClick={() => setCurrentTab("Onboarding Rules")}
							>
								<a>Onboarding Rules</a>
							</li>
						)}
					</ul>
				</div>
			</nav>
			<MainSection>
				{currentTab === "Overview" && (
					<OrgOverview
						org={org}
						currentDefaultBankName={currentDefaultBankName}
						customers={customers}
						bankNames={bankNames}
						refresh={refresh}
					/>
				)}
				<OnboardingRulesNavigationsTabContent
					clientOrgBankAgreements={clientOrgBankAgreements}
					currentTab={currentTab}
					org={org}
					refresh={refresh}
					refreshToken={refreshToken}
				/>
			</MainSection>
		</>
	) : (
		<OrgPageWithoutOnboardingRules
			org={org}
			customers={customers}
			refresh={refresh}
			currentDefaultBankName={currentDefaultBankName}
			bankNames={bankNames}
		/>
	)
}

interface OrgPageForUnitUserProps {
	org: Org
	currentDefaultBankName?: string
	customers: Customer[]
	bankNames: string[]
	refresh: () => void
	refreshToken: number
}

export default function OrgPageForUnitUser({
	org,
	currentDefaultBankName,
	customers,
	bankNames,
	refresh,
	refreshToken,
}: OrgPageForUnitUserProps) {
	const accessToken = useAccessToken()
	const clientOrgBankAgreements = useAsyncResult(
		() => findClientOrgBankAgreement(accessToken, [org.id]),
		[refreshToken]
	)

	return (
		<AsyncResultComponent asyncResult={clientOrgBankAgreements} pendingComponent={<Loading />}>
			{({value: clientOrgBankAgreements}) => {
				return (
					<Page
						org={org}
						currentDefaultBankName={currentDefaultBankName}
						customers={customers}
						bankNames={bankNames}
						refresh={refresh}
						refreshToken={refreshToken}
						clientOrgBankAgreements={clientOrgBankAgreements}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}
